import { useEffect, useRef, useState } from "react";
import { SnapCarrouselProps } from "../SnapCarrousel.types";
import { Skeleton } from "@commonlibs";

export default function useSnapCarousel<SP extends Record<string, unknown>>({
    children,
    isLoading,
    Skeleton: SkeletonProp = Skeleton,
    skeltonProps = {} as SP,
    numberOfSkeletons = 3,
    autoScroll = false,
    autoScrollInterval = 4000,
}: SnapCarrouselProps<SP>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const currentPageRef = useRef(0);

    const [currentPage, setCurrentPage] = useState<number>(0);
    const hasElements = children?.length > 0 || isLoading;
    const skeletons = Array.from({ length: numberOfSkeletons }).map((_, i) => (
        <SkeletonProp
            // skipcq
            key={i}
            {...skeltonProps}
        />
    ));
    const items = isLoading ? skeletons : children;
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const resetInterval = () => {
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
        }
        scrollIntervalRef.current = setInterval(() => {
            const current = currentPageRef.current;
            const nextPage = current + 1 >= items.length ? 0 : current + 1;
            scrollToItem(nextPage);
        }, autoScrollInterval);
    };

    const onClickBanner = (index: number) => {
        scrollToItem(index);

        if (autoScroll) {
            resetInterval();
        }
    };

    /* istanbul ignore next */
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return undefined;
        }

        const handleScroll = () => {
            const children = Array.from(container.children) as HTMLElement[];
            let closestIndex = 0;
            let closestDistance = Infinity;

            children.forEach((child, index) => {
                const offset = Math.abs(child.offsetLeft - container.scrollLeft);
                if (offset < closestDistance) {
                    closestDistance = offset;
                    closestIndex = index;
                }
            });
            if (autoScroll) {
                resetInterval();
            }
            currentPageRef.current = closestIndex;
            setCurrentPage(closestIndex);
        };

        container.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [autoScroll, children.length]);

    const scrollToItem = (index: number) => {
        currentPageRef.current = index;
        const container = containerRef.current;
        const el = itemRefs.current[index];
        if (container && el) {
            container.scrollTo({ left: el.offsetLeft, behavior: "smooth" });
        }

    };

    useEffect(() => {
        if (!autoScroll || children.length <= 1) return undefined;

        resetInterval();

        return () => {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
            }
        };
    }, [autoScroll, autoScrollInterval, children]);

    return { containerRef, itemRefs, onClickBanner, items, hasElements, currentPage };
}
